import { useState } from "react";
import { useTranslation } from "react-i18next";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Image from "rc-image";
import "rc-image/assets/index.css";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import FlipIcon from "@mui/icons-material/Flip";
import PreviewCloseIcon from "@mui/icons-material/Close";
import CloseIcon from "assets/icons/close.svg?react";
import Button from "components/Button";
import HollowButton from "components/HollowButton";
import PluginNotExist from "components/PluginNotExist";
import { EScanColorMode, EScanSource } from "dto/Plugin/scan";
import styles from "./styles.module.scss";
import { FC, IScanPageVM, Props } from "./typings";
import { DPI_OPTIONS, useScanner } from "./useScanner";
import PageThumb from "./PageThumb";

const ScanDocumentModal: FC<Props> = ({
  handleClose,
  onComplete,
  defaultFileName = "skan",
}) => {
  const { t } = useTranslation();
  const s = useScanner(true);

  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const openPreview = async (page: IScanPageVM) => {
    setPreviewLoading(true);
    try {
      setPreviewSrc(await s.loadPagePreview(page));
    } catch {
      /* podgląd best-effort */
    } finally {
      setPreviewLoading(false);
    }
  };

  if (s.pluginMissing) {
    return (
      <PluginNotExist
        message={t("scanner.installMessage")}
        handleClose={handleClose}
      />
    );
  }

  const dev = s.selectedDevice;
  const isFeeder = s.settings.source === EScanSource.Feeder;
  const selectedCount = s.pages.filter((p) => p.selected).length;
  const allSelected = s.pages.length > 0 && selectedCount === s.pages.length;

  const dpiOptions = Array.from(
    new Set([
      ...(dev?.Resolutions?.length ? dev.Resolutions : DPI_OPTIONS),
      s.settings.dpi,
    ]),
  ).sort((a, b) => a - b);

  const setDevice = (e: SelectChangeEvent) =>
    s.setSettings((prev) => ({ ...prev, deviceId: e.target.value }));

  const setSource = (_: unknown, v: EScanSource | null) => {
    if (!v) return;
    s.setSettings((prev) => ({
      ...prev,
      source: v,
      duplex: v === EScanSource.Feeder ? prev.duplex : false,
    }));
  };

  const setColor = (_: unknown, v: EScanColorMode | null) => {
    if (!v) return;
    s.setSettings((prev) => ({ ...prev, colorMode: v }));
  };

  const setDpi = (e: SelectChangeEvent<number>) =>
    s.setSettings((prev) => ({ ...prev, dpi: Number(e.target.value) }));

  const handleCreatePdf = async () => {
    const file = await s.buildPdf(defaultFileName);
    if (file) {
      await onComplete(file);
      handleClose();
    }
  };

  return (
    <div className={styles.modal}>
      <div className={styles.window}>
        <div className={styles.hdr}>
          <div className={styles.title}>{t("scanner.title")}</div>
          <CloseIcon className={styles.closeIcon} onClick={handleClose} />
        </div>

        <div className={styles.settings}>
          {s.devicesLoading && s.devices.length === 0 ? (
            <div className={styles.deviceStatus}>
              <CircularProgress size={16} />
              <span>{t("scanner.searching")}</span>
            </div>
          ) : s.devices.length === 0 ? (
            <div className={styles.deviceStatus}>{t("scanner.noDevices")}</div>
          ) : (
            <FormControl size="small" className={styles.control}>
              <InputLabel id="scan-device-label">
                {t("scanner.device")}
              </InputLabel>
              <Select
                labelId="scan-device-label"
                label={t("scanner.device")}
                value={s.settings.deviceId || s.devices[0]?.Id || ""}
                onChange={setDevice}
              >
                {s.devices.map((d) => (
                  <MenuItem key={d.Id} value={d.Id}>
                    {d.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={s.settings.source}
            onChange={setSource}
          >
            <ToggleButton value={EScanSource.Flatbed}>
              {t("scanner.flatbed")}
            </ToggleButton>
            <ToggleButton
              value={EScanSource.Feeder}
              disabled={!!dev && !dev.HasFeeder}
            >
              {t("scanner.feeder")}
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControlLabel
            className={styles.duplex}
            control={
              <Checkbox
                size="small"
                checked={s.settings.duplex}
                disabled={!isFeeder || !dev?.HasDuplex}
                onChange={(e) =>
                  s.setSettings((prev) => ({
                    ...prev,
                    duplex: e.target.checked,
                  }))
                }
              />
            }
            label={t("scanner.duplex")}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            value={s.settings.colorMode}
            onChange={setColor}
          >
            <ToggleButton value={EScanColorMode.Color}>
              {t("scanner.color")}
            </ToggleButton>
            <ToggleButton value={EScanColorMode.Grayscale}>
              {t("scanner.grayscale")}
            </ToggleButton>
            <ToggleButton value={EScanColorMode.BlackWhite}>
              {t("scanner.bw")}
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" className={styles.controlSmall}>
            <InputLabel>{t("scanner.quality")}</InputLabel>
            <Select
              label={t("scanner.quality")}
              value={s.settings.dpi}
              onChange={setDpi}
            >
              {dpiOptions.map((r) => (
                <MenuItem key={r} value={r}>
                  {r} DPI
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            text={t("scanner.scan")}
            onClick={s.startScan}
            disabled={!s.settings.deviceId || s.scanning}
            loading={s.scanStarting}
          />
        </div>

        <div className={styles.body}>
          {s.pages.length === 0 && !s.scanning && (
            <div className={styles.empty}>{t("scanner.empty")}</div>
          )}

          <div className={styles.strip}>
            {s.pages.map((p, i) => (
              <PageThumb
                key={p.key}
                page={p}
                position={i}
                total={s.pages.length}
                onToggle={() => s.toggleSelect(p.key)}
                onRotate={() => s.rotatePage(p.key)}
                onRemove={() => s.removePage(p.key)}
                onReorder={s.reorder}
                onPreview={() => openPreview(p)}
              />
            ))}

            {s.scanning && (
              <div className={styles.scanningTile}>
                <CircularProgress size={26} />
                <span>
                  {t("scanner.scanningPage", { n: s.pages.length + 1 })}
                </span>
              </div>
            )}
          </div>

          {s.error && <div className={styles.error}>{t("scanner.error")}</div>}
        </div>

        <div className={styles.footer}>
          <div className={styles.footerInfo}>
            <span>
              {t("scanner.selectedCount", {
                selected: selectedCount,
                total: s.pages.length,
              })}
            </span>
            {s.pages.length > 0 && (
              <button
                type="button"
                className={styles.linkBtn}
                onClick={() => s.setAllSelected(!allSelected)}
              >
                {allSelected
                  ? t("scanner.deselectAll")
                  : t("scanner.selectAll")}
              </button>
            )}
          </div>

          <div className={styles.footerActions}>
            {s.scanning && (
              <Button
                color="error"
                text={t("scanner.stop")}
                onClick={s.stopScan}
              />
            )}
            <HollowButton text={t("scanner.cancel")} onClick={handleClose} />
            <Button
              text={t("scanner.createPdf")}
              onClick={handleCreatePdf}
              disabled={selectedCount === 0 || s.building}
              loading={s.building}
            />
          </div>
        </div>

        {previewLoading && (
          <div className={styles.previewLoading}>
            <CircularProgress />
          </div>
        )}
        {previewSrc && (
          <Image
            src={previewSrc}
            style={{ display: "none" }}
            preview={{
              visible: true,
              src: previewSrc,
              closeIcon: <PreviewCloseIcon />,
              icons: {
                zoomIn: <ZoomInIcon fontSize="small" />,
                zoomOut: <ZoomOutIcon fontSize="small" />,
                rotateLeft: <RotateLeftIcon fontSize="small" />,
                rotateRight: <RotateRightIcon fontSize="small" />,
                flipX: <FlipIcon fontSize="small" />,
                flipY: (
                  <FlipIcon
                    fontSize="small"
                    style={{ transform: "rotate(90deg)" }}
                  />
                ),
                close: <PreviewCloseIcon />,
              },
              onVisibleChange: (v: boolean) => {
                if (!v) setPreviewSrc(null);
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default ScanDocumentModal;
